import { Outlet, Link, createRootRouteWithContext } from "@tanstack/react-router";
import { QueryClient } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/sonner";
import { useEffect } from "react";
import { OfflineBanner } from "@/components/OfflineBanner";
import { UpdateBanner } from "@/components/UpdateBanner";
import { useAppStore } from "@/store/useAppStore";
import { applyResolvedTheme, resolveTheme, watchSystemTheme } from "@/lib/theme";

interface RouterContext {
  queryClient: QueryClient;
}

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
});

function RootComponent() {
  const theme = useAppStore((s) => s.theme);

  // Mantiene <html> sincronizado con la preferencia del usuario. El primer
  // pintado ya lo resolvió el script anti-FOUC de index.html; esto cubre los
  // cambios posteriores y, en modo "system", los cambios del SO en vivo.
  useEffect(() => {
    applyResolvedTheme(resolveTheme(theme));
    if (theme !== "system") return;
    return watchSystemTheme(() => applyResolvedTheme(resolveTheme("system")));
  }, [theme]);

  useEffect(() => {
    const el = document.getElementById("app-loading");
    if (el) {
      el.classList.add("hidden");
      setTimeout(() => el.remove(), 350);
    }
  }, []);

  return (
    <>
      <OfflineBanner />
      <UpdateBanner />
      <Outlet />
      <Toaster richColors position="top-right" />
    </>
  );
}
